 
import {
  createContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
  type ReactNode,
} from "react";
import {
  getCurrentUser,
  fetchAuthSession,
  signOut as amplifySignOut,
  type AuthUser,
  type AuthSession,
} from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
import { getAccess, type UserAccess } from "../services";

interface AuthState {
  authUser: AuthUser | null;
  user: UserAccess | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

export interface AuthContextType extends AuthState {
  checkAuth: () => Promise<void>;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

const initialState: AuthState = {
  authUser: null,
  user: null,
  isAuthenticated: false,
  isLoading: true,
};

const clearState: AuthState = {
  authUser: null,
  user: null,
  isAuthenticated: false,
  isLoading: false,
};

const getAuthUserFromToken = (session: AuthSession): AuthUser | null => {
  const payload = session.tokens?.idToken?.payload;
  if (!payload) return null;

  return {
    userId: payload.sub as string,
    username:
      (payload["cognito:username"] as string) || (payload.email as string),
  } as AuthUser;
};

const getEmailFromSession = (session: AuthSession): string | null => {
  return (session.tokens?.idToken?.payload?.email as string) || null;
};

const hasOAuthCallback = (): boolean => {
  const searchParams = new URLSearchParams(globalThis.location?.search || "");
  const code = searchParams.get("code");
  const error = searchParams.get("error");

  // Backward compatibility (implicit flow / transition)
  const hash = globalThis.location?.hash || "";
  const hasTokensInHash = hash.includes("access_token") || hash.includes("id_token");

  return !!(code || error || hasTokensInHash);
};

/**
 * Limpia todos los tokens de Cognito del storage para prevenir loops de redirección
 * y problemas de autenticación en sesiones futuras.
 */
const clearCognitoTokens = (): void => {
  try {
    const keysToRemove: string[] = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (
        key.includes("CognitoIdentityServiceProvider") ||
        key.includes("aws-amplify") ||
        key.includes("amplify-") ||
        key.includes("CognitoUserPool")
      )) {
        keysToRemove.push(key);
      }
    }
    
    keysToRemove.forEach((key) => {
      localStorage.removeItem(key);
    });
    
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && (
        key.includes("CognitoIdentityServiceProvider") ||
        key.includes("aws-amplify") ||
        key.includes("amplify-") ||
        key.includes("CognitoUserPool")
      )) {
        sessionStorage.removeItem(key);
      }
    }
    
    if (hasOAuthCallback()) {
      try {
        const url = new URL(globalThis.location?.href || "");
        url.searchParams.delete("code");
        url.searchParams.delete("error");
        url.searchParams.delete("state");
        url.hash = "";
        globalThis.history?.replaceState(null, "", url.pathname + url.search);
      } catch {
        globalThis.history?.replaceState(null, "", globalThis.location?.pathname);
      }
    }
  } catch (error) {
    console.error("Error clearing Cognito tokens:", error);
  }
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [authState, setAuthState] = useState<AuthState>(initialState);
  const isMountedRef = useRef(true);
  const isInitializedRef = useRef(false);
  const shouldNavigateTo403Ref = useRef(false);

  const fetchUser = useCallback(
    async (email: string): Promise<UserAccess | null> => {
      try {
        const { data } = await getAccess(email);
        return data;
      } catch (error) {
        console.error("Error fetching user:", error);
        return null;
      }
    },
    []
  );

  const processSession = useCallback(
    async (session: AuthSession): Promise<AuthState> => {
      if (!session.tokens?.accessToken) {
        return clearState;
      }

      let authUser: AuthUser | null = null;

      try {
        authUser = await getCurrentUser();
      } catch {
        authUser = getAuthUserFromToken(session);
      }

      const email = getEmailFromSession(session);

      if (!email) {
        console.warn("No email found in token payload");
        return clearState;
      }

      const user = await fetchUser(email);

      // Si el usuario no tiene rol, marcar para navegar a página 403
      if (!user?.rol) {
        shouldNavigateTo403Ref.current = true;
        return clearState;
      }

      shouldNavigateTo403Ref.current = false;
      return {
        authUser,
        user,
        isAuthenticated: true,
        isLoading: false,
      };
    },
    [fetchUser]
  );

  const checkSession = useCallback(async () => {
    if (!isMountedRef.current) return;

    try {
      setAuthState((prev) => ({ ...prev, isLoading: true }));

      const session = await fetchAuthSession();

      if (!isMountedRef.current) return;

      const newState = await processSession(session);

      if (isMountedRef.current) {
        setAuthState(newState);
        
        // Si no hay rol, navegar a página 403
        // Verificar que no estemos ya en /error para evitar loops
        if (shouldNavigateTo403Ref.current && !globalThis.location.pathname.includes('/error')) {
          shouldNavigateTo403Ref.current = false;
          const params = new URLSearchParams({
            code: "403",
            title: "Acceso denegado",
            message: "No tienes permisos para acceder a esta aplicación. Por favor, contacta al administrador.",
            buttonText: "Cerrar sesión",
          });
          // Usar replace para evitar que se pueda volver atrás
          globalThis.location.replace(`/error?${params.toString()}`);
        }
      }
    } catch (error) {
      console.error("Error checking session:", error);
      if (isMountedRef.current) {
        setAuthState(clearState);
      }
    }
  }, [processSession]);

  const signOut = useCallback(async () => {
    try {
      // Limpiar tokens primero para prevenir loops de redirección
      clearCognitoTokens();
      await amplifySignOut({ global: true });
      
      // Actualizar estado de forma asíncrona para evitar loops de renderizado
      setTimeout(() => {
        if (isMountedRef.current) {
          setAuthState(clearState);
        }
      }, 0);
    } catch (error) {
      console.error("Error signing out:", error);
      clearCognitoTokens();
      setTimeout(() => {
        if (isMountedRef.current) {
          setAuthState(clearState);
        }
      }, 0);
    }
  }, []);

  const refreshUser = useCallback(async () => {
    if (!authState.isAuthenticated) return;

    try {
      const session = await fetchAuthSession();
      const email = getEmailFromSession(session);

      if (email) {
        const user = await fetchUser(email);
        if (isMountedRef.current) {
          setAuthState((prev) => ({ ...prev, user }));
        }
      }
    } catch (error) {
      console.error("Error refreshing user:", error);
    }
  }, [authState.isAuthenticated, fetchUser]);

  useEffect(() => {
    isMountedRef.current = true;

    const hubListener = Hub.listen("auth", async ({ payload }) => {
      if (!isMountedRef.current) return;

      switch (payload.event) {
        case "signInWithRedirect":
        case "tokenRefresh":
          await checkSession();
          break;
        case "signInWithRedirect_failure":
        case "tokenRefresh_failure":
        case "signedOut":
          setAuthState(clearState);
          break;
      }
    });

    // Si hay tokens OAuth en hash, LoginPage los procesará
    // Mantener isLoading en true para que LoginPage muestre loading
    const initAuth = async () => {
      if (isInitializedRef.current) return;
      isInitializedRef.current = true;

      // No ejecutar checkSession si ya estamos en la página de error
      // Esto previene loops cuando se navega a /error por falta de rol
      if (globalThis.location.pathname.includes('/error')) {
        return;
      }

      if (hasOAuthCallback()) {
        return;
      }

      await checkSession();
    };

    initAuth();

    return () => {
      isMountedRef.current = false;
      hubListener();
    };
  }, [checkSession]);

  const contextValue = useMemo<AuthContextType>(
    () => ({
      ...authState,
      checkAuth: checkSession,
      signOut,
      refreshUser,
    }),
    [authState, checkSession, signOut, refreshUser]
  );

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

export default AuthContext;
